import { Progress } from 'antd'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflows } from 'features/libra/ticket/libraTicketsApi'
import { useRolodexEntries } from 'features/rolodex'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

type DashboardSummaryChartsProps = {
  tickets: ReadonlyArray<LibraTicket>
  organizationId: string
}

export const DashboardSummaryCharts: FC<DashboardSummaryChartsProps> = ({
  tickets,
  organizationId,
}) => {
  // Get unique unit IDs from tickets
  const unitIdsWithTickets = useMemo(() => {
    return new Set(
      tickets
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )
  }, [tickets])

  // Query all units in the organization
  const { entries: allUnits } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.UNIT,
        tags: { organization: organizationId },
      }),
      [organizationId]
    )
  )

  // Get workflow IDs from tickets
  const workflowIds = useMemo(() => {
    return Array.from(
      new Set(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      )
    )
  }, [tickets])

  const { libraWorkflows } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  // Calculate aggregate completion
  const aggregateCompletion = useMemo(() => {
    if (tickets.length === 0) return 0

    let totalProgress = 0
    let validTickets = 0

    tickets.forEach((ticket) => {
      const workflow = workflowMap.get(ticket.workflowId)
      if (!workflow) return

      const currentIndex = workflow.stepIndices.get(ticket.stepId) ?? -1
      const totalSteps = workflow.steps.length

      if (ticket.workflowComplete) {
        totalProgress += 100
        validTickets++
      } else if (currentIndex >= 0) {
        totalProgress += (currentIndex / totalSteps) * 100
        validTickets++
      }
    })

    return validTickets > 0 ? Math.round(totalProgress / validTickets) : 0
  }, [tickets, workflowMap])

  const assetCoverage = useMemo(() => {
    const totalAssets = allUnits.length
    const assetsWithTickets = unitIdsWithTickets.size
    return totalAssets > 0
      ? Math.round((assetsWithTickets / totalAssets) * 100)
      : 0
  }, [allUnits, unitIdsWithTickets])

  return (
    <ChartsContainer>
      <ChartCard>
        <ChartContent>
          <Progress
            type='dashboard'
            percent={assetCoverage}
            size={60}
            strokeWidth={8}
            strokeColor={{
              '0%': '#ff6b6b',
              '100%': '#52c41a',
            }}
            format={() => <ProgressValue>{assetCoverage}%</ProgressValue>}
          />
        </ChartContent>
        <ChartMeta>
          <MetaTitle>Asset Coverage</MetaTitle>
          <MetaSubtext>
            {unitIdsWithTickets.size} / {allUnits.length} assets
          </MetaSubtext>
        </ChartMeta>
      </ChartCard>

      <ChartCard>
        <ChartContent>
          <Progress
            type='dashboard'
            percent={aggregateCompletion}
            size={60}
            strokeWidth={8}
            strokeColor={{
              '0%': '#ff6b6b',
              '100%': '#52c41a',
            }}
            format={() => <ProgressValue>{aggregateCompletion}%</ProgressValue>}
          />
        </ChartContent>
        <ChartMeta>
          <MetaTitle>Overall Progress</MetaTitle>
          <MetaSubtext>{tickets.length} tickets</MetaSubtext>
        </ChartMeta>
      </ChartCard>
    </ChartsContainer>
  )
}

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
`

const ChartCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${({ theme }) => theme.colors.gray[800]};
  padding: 0.625rem 0.875rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const ChartContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ChartMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const MetaTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const MetaSubtext = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  white-space: nowrap;
`

const ProgressValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`
